import { z } from 'zod';

const FieldResponseGraphQlConfigSchema = z.object({
  field_id: z.string(),
  app_model_name: z.string(),
  model_field_name: z.string()
});

const DependedFieldSchema = z.object({
  id: z.string(),
  opted_value: z.string().optional()
});

const FieldSchema = z.object({
  id: z.string(),
  type: z.enum([
    'PLAIN_TEXT',
    'PHONE_NUMBER',
    'EMAIL',
    'NUMBER',
    'FLOAT',
    'LONG_TEXT',
    'DROPDOWN',
    'RADIO_GROUP',
    'CHECKBOX_GROUP',
    'MULTI_SELECT',
    'DATE',
    'FILE_UPLOADER',
    'COMMON_DATA_SEARCH',
    'CHECKBOX'
  ]),
  name: z.string(),
  label: z.string().optional(),
  label_helper_text: z.string().optional(),
  placeholder_text: z.string().optional(),
  validation_config: z.string().optional(),
  helper_text: z.string().optional(),
  default_value: z.string().optional(),
  is_required: z.boolean(),
  extra_config: z.string().optional(),
  response_submission_type: z
    .enum([
      'OTHER_APP_WITH_GRAPHQL',
      'TEMPLATE_FIELD_RESPONSE',
      'ACCOUNTS_API_WITH_GRAPHQL'
    ])
    .optional(),
  order: z.number().optional(),
  field_response_graph_ql_config: FieldResponseGraphQlConfigSchema.optional(),
  depended_fields: z.array(DependedFieldSchema).optional()
});

const GroupConfigFieldSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  order: z.number(),
  display_type: z.enum(['BLOCK', 'IN_LINE']),
  fields: z.array(FieldSchema)
});

const TemplateConfigDetailSchema = z.object({
  template_id: z.string(),
  order: z.number(),
  name: z.string(),
  extra_config: z.string(),
  group_config_fields: z.array(GroupConfigFieldSchema).optional()
});

export const GetIbFormDetailsResponseSchema = z.object({
  form_config: z.string(),
  form_submission_message_config: z.string(),
  form_field_config: z.string(),
  form_info: z.string(),
  form_validation_config: z.string(),
  is_submission_edit_allowed: z.boolean(),
  allowed_no_of_times_to_submit: z.number().optional(),
  on_submit_redirect_url: z.string().optional(),
  template_config_details: z.array(TemplateConfigDetailSchema).optional()
});

export type GetIbFormDetailsResponseType = z.infer<
  typeof GetIbFormDetailsResponseSchema
>;

export const GetIbFormDetailsRequestSchema = z.object({
  form_id: z.string()
});

export type GetIbFormDetailsRequestType = z.infer<
  typeof GetIbFormDetailsRequestSchema
>;

export const GetIbFormSubmissionDetailsRequestSchema = z.object({
  form_id: z.string()
});

export type GetIbFormSubmissionDetailsRequestType = z.infer<
  typeof GetIbFormSubmissionDetailsRequestSchema
>;

export const GetIbFormSubmissionDetailsResponseSchema = z.object({
  form_submission_details: z.array(
    z.object({
      form_submission_id: z.number(),
      creation_datetime: z.string(),
      last_updated_datetime: z.string(),
      form_submission_data: z.string()
    })
  )
});

export type GetIbFormSubmissionDetailsResponseType = z.infer<
  typeof GetIbFormSubmissionDetailsResponseSchema
>;

export const GetPublicIbFormDetailsRequestSchema = z.object({
  form_id: z.string()
});

export type GetPublicIbFormDetailsRequestType = z.infer<
  typeof GetPublicIbFormDetailsRequestSchema
>;

export const GetPublicIbFormDetailsResponseSchema = z.object({
  form_config: z.string(),
  form_submission_message_config: z.string(),
  form_field_config: z.string(),
  form_info: z.string(),
  form_validation_config: z.string(),
  is_submission_edit_allowed: z.boolean(),
  allowed_no_of_times_to_submit: z.number().optional(),
  on_submit_redirect_url: z.string().optional()
});

export type GetPublicIbFormDetailsResponseType = z.infer<
  typeof GetPublicIbFormDetailsResponseSchema
>;

export const CreateIbFormSubmissionRequestSchema = z.object({
  form_id: z.string(),
  form_submission_id: z.number().optional(),
  form_submission_data: z.string(),
  submission_action: z
    .enum(['OVERRIDE_OLD_SUBMISSION', 'CREATE_AS_NEW', 'UPDATE_OLD_SUBMISSION'])
    .optional()
});

export type CreateIbFormSubmissionRequestType = z.infer<
  typeof CreateIbFormSubmissionRequestSchema
>;

export const CreateIbFormSubmissionResponseSchema = z.object({
  ib_form_submission_id: z.string()
});

export type CreateIbFormSubmissionResponseType = z.infer<
  typeof CreateIbFormSubmissionResponseSchema
>;

export const CreatePublicIbFormSubmissionRequestSchema = z.object({
  form_id: z.string(),
  form_submission_data: z.string(),
  phone_number_details: z
    .object({
      phone_number: z.string(),
      country_code: z.string()
    })
    .optional()
});

export type CreatePublicIbFormSubmissionRequestType = z.infer<
  typeof CreatePublicIbFormSubmissionRequestSchema
>;

export const CreatePublicIbFormSubmissionResponseSchema = z.object({
  form_submission_id: z.string()
});

export type CreatePublicIbFormSubmissionResponseType = z.infer<
  typeof CreatePublicIbFormSubmissionResponseSchema
>;

export const FormReferralCodeStatusEnum = z.enum(['ACTIVE', 'EXPIRED']);

export const GetFormReferralCodeResponseSchema = z.object({
  referral_code: z.string().nullable(),
  status: FormReferralCodeStatusEnum.nullable()
});

export type GetFormReferralCodeResponseType = z.infer<
  typeof GetFormReferralCodeResponseSchema
>;

export const UserFormReferralTrackRequestSchema = z.object({
  form_id: z.string(),
  existing_licence_code: z.string(),
  attempted_licence_code: z.string()
});

export type UserFormReferralTrackRequestType = z.infer<
  typeof UserFormReferralTrackRequestSchema
>;

export const UserFormReferralTrackResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number()
});

export type UserFormReferralTrackResponseType = z.infer<
  typeof UserFormReferralTrackResponseSchema
>;
